<template>
  <div class="candidate-testing">
    <div class="candidate-testing__header">
      <div style="margin-right: 10px;">
        {{ testPassedText }}
      </div>
      <div>
        <span class="red-text" :class="{ 'green-text': testPassed }">{{ correctQuestionCount }}</span>/{{ testQuestions.length }}
      </div>
    </div>
    <div class="candidate-testing__info">
      <div v-if="testTimer" class="candidate-testing__info_time">
        <span>{{ $t('timeSpent') }}:</span> <span>{{ testTimePassed.min }}:{{ testTimePassed.sec }}</span>
      </div>
      <div class="candidate-testing__info_points">
        <div>
          <span>{{ $t('pointsScored') }}: </span><span>{{ testResultPercent }}% ({{ testResultScore }})</span>
        </div>
        <div v-if="testTypeCutoff">
          <span>{{ $t('passing') }}: </span><span>{{ testMinimalScore }}%</span>
        </div>
      </div>
    </div>
    <VDivider class="my-4" />
    <div class="candidate-testing__questions-block">
      <div v-for="(q, index) in testQuestions" :key="q.order" class="mb-4">
        <CandidateTestResultQuestion
          :question="q"
          :candidate-id="candidate.id"
          :index="index"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed, ref, toRefs,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { testQuestionTypes, testTypes } from '@/enums';
import CandidateTestResultQuestion from '@/components/Candidate/CandidateTestResultQuestion.vue';
import useCandidates from '@/compositions/useCandidates';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'CandidateTestResult',
  components: {
    CandidateTestResultQuestion,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { candidate } = toRefs(props);
    const { t } = useI18n();
    const { getTestResult } = useCandidates();

    const testResult = getTestResult(candidate.value.id);

    const questionImage = ref(null);

    const testTimer = computed(() => testResult.value?.test.timer);
    const testQuestions = computed(() => {
      const questions = cloneDeep(testResult.value?.test.questions);
      return questions.sort((a, b) => a.order - b.order);
    });
    const testAnswers = computed(() => testResult.value?.testAnswers);
    const totalPoints = computed(() => (testQuestions.value.reduce((sum, q) => sum + Number(q.score), 0)));
    const testResultScore = computed(() => testResult.value?.testResult.score);
    const testResultPercent = computed(() => Math.ceil((testResultScore.value * 100) / totalPoints.value));
    const testMinimalScore = computed(() => testResult.value?.test.minimalScore);
    const testTypeCutoff = computed(() => testResult.value?.test.type === testTypes.CUTOFF);

    function isAnswerCorrect(answer, correctAnswer) {
      return answer.answer === correctAnswer.answer;
    }

    function isSelectCorrect(answer, correctAnswer) {
      return answer.answer === correctAnswer.id.toString();
    }

    function isMultiselectCorrect(answer, qAnswers) {
      let correct = true;
      const allCorrectId = qAnswers
        .filter((a) => a.isCorrect)
        .map((a) => a.id);

      const ans = answer.answer.split(';').map(Number);
      ans.forEach((a) => {
        if (!allCorrectId.includes(a)) {
          correct = false;
        }
      });

      return allCorrectId.length === ans.length && correct;
    }

    const correctQuestionCount = computed(() => {
      let result = 0;

      testQuestions.value.forEach((q) => {
        const answer = q.candidateAnswers.find((a) => a.candidateId === candidate.value.id);
        const correctAnswer = q.answers.find((a) => a.isCorrect);

        if (!answer) {
          return;
        }

        // eslint-disable-next-line default-case
        switch (q.type) {
          case testQuestionTypes.FREE: {
            result += 1;
            break;
          }
          case testQuestionTypes.TEXT: {
            result = isAnswerCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.NUMERIC: {
            result = isAnswerCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.SELECT: {
            result = isSelectCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.MULTISELECT: {
            result = isMultiselectCorrect(answer, q.answers)
              ? result + 1
              : result;
            break;
          }
        }
      });

      return result;
    });

    const testType = computed(() => {
      if (testResult.value?.test.type === testTypes.CUTOFF) {
        return t('cutoff');
      }

      return t('explanation');
    });
    const testPassed = computed(() => testResult.value?.testResult.passed);
    const testPassedText = computed(() => `${testType.value} ${t('test')} ${testPassed.value ? t('passed') : t('notPassed')}`);

    const testTimePassed = computed(() => {
      const totalSeconds = (new Date(candidate.value?.testFinishedAt).getTime() - new Date(candidate.value?.testStartedAt).getTime()) / 1000;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return {
        min: minutes < 10 ? `0${minutes}` : minutes,
        sec: seconds < 10 ? `0${seconds}` : seconds,
      };
    });

    return {
      testPassed,
      testPassedText,
      testQuestions,
      testAnswers,
      testTimer,
      testTimePassed,
      questionImage,
      testResultScore,
      totalPoints,
      testResultPercent,
      testMinimalScore,
      correctQuestionCount,
      testQuestionTypes,
      testTypeCutoff,
    };
  },
};
</script>

<style lang="scss" scoped>
.candidate-testing {
  padding: 16px;

  &__header {
    display: flex;
    font-size: 14px;
    line-height: 16.8px;
    font-weight: 400;
    margin-bottom: 5px;
    @media screen and (max-width: 428px) {
      justify-content: space-between;
    }
  }

  &__info {
    color: #929292;
    font-size: 14px;
    line-height: 16.8px;
    display: flex;
    @media screen and (max-width: 1024px) {
      flex-direction: column;
      gap: 3px;
    }

    &_time {
      margin-right: 20px;
      @media screen and (max-width: 599px) {
        margin-right: 0;
      }
      @media screen and (max-width: 428px) {
        display: flex;
        justify-content: space-between;
      }
    }
    &_points {
      display: flex;
      div {
        margin-right: 20px;
        @media screen and (max-width: 428px) {
          display: flex;
          justify-content: space-between;
          margin-right: 0;
        }
      }
      @media screen and (max-width: 428px) {
        flex-direction: column;
      }
    }
  }

  &__question {
    font-size: 14px;
    line-height: 20px;
    &_img-btn {
      display: inline-block;
      cursor: pointer;
      margin-left: 3px;
    }
  }

  &__answer-block {
    margin-top: 7px;
  }

  &__questions-block {
    height: 360px;
    overflow: auto;

    @media screen and (max-width: 959px) {
      overflow: hidden;
      height: 100%;
    }
  }

  &__answer {
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    @media screen and (max-width: 599px) {
      flex-direction: column;
      align-items: flex-start;
    }
    &_no-correct {
      margin-left: 24px;
      color: #929292;
      @media screen and (max-width: 599px) {
        margin-left: 0;
      }
    }
  }

  &__dialog-img-content {
    position: relative;
    padding: 50px;
    @media screen and (max-width: 599px) {
      padding: 0;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate(0, -50%);
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
}
.red-text {
  color: red;
}
.green-text {
  color: #1FC57F;
}

.close-button {
  position: absolute;
  right: 15px;
  top: 15px;
}
.v-image {
  background-color: #fff;
}
</style>

<i18n locale="en" lang="yaml">
  test: test
  passed: passed
  notPassed: not passed
  explanation: Explanation
  cutoff: Сutoff
  noAnswer: There is no answer
  timeSpent: Time elapsed
  correctAnswer: correct answer
  pointsScored: Points earned
  outOf: out of
  passing: Passing
</i18n>

<i18n locale="ru" lang="yaml">
  test: тест
  passed: пройден
  notPassed: не пройден
  explanation: Уточняющий
  cutoff: Отсекающий
  noAnswer: Нет ответа
  timeSpent: Затрачено времени
  correctAnswer: правильный ответ
  pointsScored: Набрано баллов
  outOf: из
  passing: Проходной
</i18n>
